import React from 'react';
import { CheckboxGroup } from '@oysterjs/ui/checkbox';
import { Spinner } from '@oysterjs/ui/spinner';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Description, Label } from '@oysterjs/ui/fieldset';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Button } from '@oysterjs/ui/button';
import {
  MailboxAccountStatus,
  TriggerEmailArrivalMode as Arrival,
  TriggerEmailScopeMode as ScopeMode,
  type TriggerEmailAccountScopeInput
} from '~/api/graphql';
import {
  accountScopePath,
  EMAIL_FOLDER_ROLES,
  EMAIL_LIMITS,
  type EmailConfigDraft,
  type EmailErrors,
  type EmailOptions,
  type EmailFolders
} from '~/api/data/triggers/email-config';
import {
  BuilderField,
  Check,
  ValuesField,
  humanize,
  toggleValue
} from '~/components/triggers/email-builder/fields';
import { EMAIL_FOLDER_ROLE_LABELS } from '~/api/data/triggers/email-summary';
import { CreationEventContext } from '~/components/triggers/email-builder/creation-event';

export type FolderCatalog = {
  data?: EmailFolders;
  loading?: boolean;
  error?: string;
  retry?: () => void;
};

const AccountFolders = ({
  account,
  path,
  catalog,
  errors,
  onChange
}: {
  account: TriggerEmailAccountScopeInput;
  path: string;
  catalog?: FolderCatalog;
  errors: EmailErrors;
  onChange: (account: TriggerEmailAccountScopeInput) => void;
}) => {
  const folders = account.folders;
  const update = (next: typeof folders) => onChange({ ...account, folders: next });
  return (
    <div className="space-y-3">
      {(!catalog || catalog.loading) && (
        <div
          role="status"
          className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400"
        >
          <Spinner size="size-4" color="neutral" />
          Loading folders… You can save once they’ve loaded.
        </div>
      )}
      {catalog?.error && (
        <div role="alert">
          <p>Unable to load folders: {catalog.error}</p>
          {catalog.retry && (
            <Button plain type="button" onClick={catalog.retry}>
              Reload folders
            </Button>
          )}
        </div>
      )}
      <BuilderField
        label="Folders"
        path={`${path}.folders`}
        errors={errors}
        description="Choose which folders to watch in this mailbox. Drafts never match."
      >
        {(props) => (
          <CheckboxGroup
            id={props.id}
            role="group"
            aria-labelledby={props['aria-labelledby']}
            aria-describedby={props['aria-describedby']}
            aria-invalid={props['aria-invalid']}
          >
            <Check
              label="All folders"
              checked={folders.allFolders ?? false}
              onChange={(checked) =>
                update({ ids: [], allFolders: checked, includeJunkAndDeleted: false })
              }
            />
            {folders.allFolders ? (
              <Check
                label="Include Junk Email and Trash folders"
                checked={folders.includeJunkAndDeleted ?? false}
                onChange={(checked) => update({ ...folders, includeJunkAndDeleted: checked })}
              />
            ) : (
              <>
                {catalog?.data?.length === 0 && !catalog.loading && !catalog.error && (
                  <p role="status">No folders are available for this mailbox.</p>
                )}
                {catalog?.data?.map((folder) => (
                  <Check
                    key={folder.id}
                    label={`${folder.path || folder.name}${folder.role?.toUpperCase() === 'DRAFTS' ? ' (excluded)' : ''}`}
                    checked={folders.ids?.includes(folder.id) ?? false}
                    disabled={
                      folder.role?.toUpperCase() === 'DRAFTS' && !folders.ids?.includes(folder.id)
                    }
                    onChange={(checked) =>
                      update({
                        ...folders,
                        ids: toggleValue(folders.ids ?? [], folder.id, checked)
                      })
                    }
                  />
                ))}
                {(folders.ids ?? [])
                  .filter((id) => catalog?.data && !catalog.data.some((folder) => folder.id === id))
                  .map((id) => (
                    <Check
                      key={id}
                      label="Unavailable folder (remove it to choose another)"
                      checked
                      onChange={() =>
                        update({
                          ...folders,
                          ids: (folders.ids ?? []).filter((item) => item !== id)
                        })
                      }
                    />
                  ))}
              </>
            )}
          </CheckboxGroup>
        )}
      </BuilderField>
      {errors[`${path}.folders.ids`] && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {errors[`${path}.folders.ids`]}
        </p>
      )}
    </div>
  );
};

export const CreationWhen = ({
  config,
  options,
  catalogs,
  errors,
  onChange,
  eventField
}: {
  config: EmailConfigDraft;
  options: EmailOptions;
  catalogs: Readonly<Record<string, FolderCatalog | undefined>>;
  errors: EmailErrors;
  onChange: (value: EmailConfigDraft) => void;
  eventField?: React.ReactNode;
}) => {
  const creation = React.useContext(CreationEventContext);
  const scope = config.scope;
  const accounts = scope.accounts ?? [];
  const updateScope = (next: typeof scope) => onChange({ ...config, scope: next });
  const all = scope.folders ?? {
    standard: [],
    paths: [],
    allFolders: false,
    includeJunkAndDeleted: false
  };
  return (
    <div className="space-y-6">
      {eventField ??
        (creation ? null : (
          <div className="max-w-md">
            <BuilderField label="Event" path="event" errors={errors}>
              {(props) => <Input {...props} readOnly value="An incoming mailbox email arrives" />}
            </BuilderField>
          </div>
        ))}
      <BuilderField
        label="Mailboxes"
        path="scope"
        errors={errors}
        description="Only eligible connected Outlook mailboxes participate and drafts are always excluded. Changing this clears the current mailbox and folder selection."
      >
        {(props) => (
          <RadioGroup
            value={scope.mode}
            aria-labelledby={props['aria-labelledby']}
            aria-describedby={props['aria-describedby']}
            aria-invalid={props['aria-invalid']}
            onChange={(mode: ScopeMode) =>
              updateScope(
                mode === ScopeMode.All
                  ? {
                      mode: ScopeMode.All,
                      accounts: [],
                      folders: {
                        standard: [],
                        paths: [],
                        allFolders: false,
                        includeJunkAndDeleted: false
                      }
                    }
                  : { mode: ScopeMode.Selected, accounts: [], folders: null }
              )
            }
          >
            <RadioField>
              <Radio value={ScopeMode.Selected} color="sky" />
              <Label>Selected mailboxes</Label>
              <Description>Watch only the connected mailboxes you choose below.</Description>
            </RadioField>
            <RadioField>
              <Radio value={ScopeMode.All} color="sky" />
              <Label>All current and future eligible mailboxes</Label>
              <Description>Includes mailboxes connected after this trigger is created.</Description>
            </RadioField>
          </RadioGroup>
        )}
      </BuilderField>
      {scope.mode === ScopeMode.Selected ? (
        <BuilderField
          label="Choose mailboxes"
          path="scope.accounts"
          errors={errors}
          description="Pick the connected mailboxes this trigger watches."
        >
          {(props) => (
            <CheckboxGroup
              id={props.id}
              role="group"
              aria-labelledby={props['aria-labelledby']}
              aria-describedby={props['aria-describedby']}
              aria-invalid={props['aria-invalid']}
            >
              {!options.accounts.length && (
                <p role="status" className="m-0 text-sm text-neutral-500 dark:text-neutral-400">
                  No connected mailboxes are available. Connect one in Settings, or configure an
                  explicit all-mailbox folder scope for future eligible mailboxes.
                </p>
              )}
              {options.accounts.map((option) => {
                const index = accounts.findIndex((account) => account.accountId === option.id);
                const account = index >= 0 ? accounts[index] : undefined;
                const accountPath = accountScopePath(index);
                const accountError = account ? errors[accountPath] : undefined;
                return (
                  <div key={option.id} className="space-y-4">
                    <Check
                      label={option.address}
                      description={`${humanize(option.status)} · Owner: ${
                        options.users.find((user) => user.id === option.ownerUserId)?.name ??
                        'Unavailable owner'
                      }`}
                      checked={!!account}
                      disabled={
                        !account &&
                        (option.status !== MailboxAccountStatus.Connected ||
                          accounts.length >= EMAIL_LIMITS.values)
                      }
                      onChange={(checked) =>
                        updateScope({
                          ...scope,
                          accounts: checked
                            ? [
                                ...accounts,
                                {
                                  accountId: option.id,
                                  folders: {
                                    ids: [],
                                    allFolders: false,
                                    includeJunkAndDeleted: false
                                  }
                                }
                              ]
                            : accounts.filter((item) => item.accountId !== option.id)
                        })
                      }
                    />
                    {account && (
                      <div className="ml-7 space-y-4 border-l border-neutral-200 pl-4 dark:border-neutral-700">
                        {option.status !== MailboxAccountStatus.Connected && (
                          <p role="alert" className="m-0 text-sm text-red-600 dark:text-red-400">
                            This selected mailbox is no longer eligible. Remove it before saving.
                          </p>
                        )}
                        {accountError && (
                          <p role="alert" className="m-0 text-sm text-red-600 dark:text-red-400">
                            {accountError}
                          </p>
                        )}
                        <AccountFolders
                          account={account}
                          path={accountPath}
                          catalog={catalogs[option.id]}
                          errors={errors}
                          onChange={(next) =>
                            updateScope({
                              ...scope,
                              accounts: accounts.map((item) =>
                                item.accountId === option.id ? next : item
                              )
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              {accounts
                .filter(
                  (account) => !options.accounts.some((option) => option.id === account.accountId)
                )
                .map((account) => (
                  <Check
                    key={account.accountId}
                    label="Unavailable mailbox (remove it to choose another)"
                    checked
                    onChange={() =>
                      updateScope({
                        ...scope,
                        accounts: accounts.filter((item) => item.accountId !== account.accountId)
                      })
                    }
                  />
                ))}
            </CheckboxGroup>
          )}
        </BuilderField>
      ) : (
        <div className="space-y-6">
          <CheckboxGroup>
            <Check
              label="All folders"
              checked={all.allFolders ?? false}
              onChange={(checked) =>
                updateScope({
                  ...scope,
                  folders: {
                    standard: [],
                    paths: [],
                    allFolders: checked,
                    includeJunkAndDeleted: false
                  }
                })
              }
            />
            {all.allFolders && (
              <Check
                label="Include Junk Email and Trash folders"
                checked={all.includeJunkAndDeleted ?? false}
                onChange={(checked) =>
                  updateScope({ ...scope, folders: { ...all, includeJunkAndDeleted: checked } })
                }
              />
            )}
          </CheckboxGroup>
          {!all.allFolders && (
            <>
              <BuilderField
                label="Standard folder roles"
                path="scope.folders.standard"
                errors={errors}
              >
                {(field) => (
                  <CheckboxGroup
                    id={field.id}
                    role="group"
                    aria-labelledby={field['aria-labelledby']}
                    aria-describedby={field['aria-describedby']}
                    aria-invalid={field['aria-invalid']}
                  >
                    {EMAIL_FOLDER_ROLES.map((role) => (
                      <Check
                        key={role}
                        label={EMAIL_FOLDER_ROLE_LABELS[role]}
                        checked={all.standard?.includes(role) ?? false}
                        onChange={(checked) =>
                          updateScope({
                            ...scope,
                            folders: {
                              ...all,
                              standard: toggleValue(all.standard ?? [], role, checked)
                            }
                          })
                        }
                      />
                    ))}
                  </CheckboxGroup>
                )}
              </BuilderField>
              <ValuesField
                label="Full folder paths"
                path="scope.folders.paths"
                errors={errors}
                values={all.paths ?? []}
                description="Paths use / between folder levels. They are resolved for each current or future eligible mailbox."
                onChange={(paths) => updateScope({ ...scope, folders: { ...all, paths } })}
              />
            </>
          )}
          {errors['scope.folders'] && (
            <p role="alert" className="m-0 text-sm text-red-600 dark:text-red-400">
              {errors['scope.folders']}
            </p>
          )}
        </div>
      )}
      <div className="max-w-md">
        <BuilderField label="Email arrivals" path="arrivals" errors={errors}>
          {(field) => (
            <Select
              {...field}
              value={config.arrivals}
              onChange={(event) => {
                if (event.target.value === Arrival.New)
                  onChange({ ...config, arrivals: Arrival.New });
              }}
            >
              {config.arrivals !== Arrival.New && (
                <option value={config.arrivals} disabled>
                  Unsupported arrival mode
                </option>
              )}
              <option value={Arrival.New}>New email arrivals only</option>
            </Select>
          )}
        </BuilderField>
      </div>
    </div>
  );
};
